import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { PageHeader, Panel, Tag, StatusBadge } from "@/components/common";
import { Gear, ShieldCheck, Globe, UsersThree, Lock, ClockCounterClockwise, HardDrives } from "@phosphor-icons/react";

const LANGS = ["English (master)", "Turkish", "Bulgarian", "Spanish", "German", "French"];
const SECURITY = ["Server-side API calls", "Encrypted secrets", "OAuth where supported", "Role-based access", "Audit logs", "Rate limiting", "Secure storage"];

export default function Settings() {
  const { user, logout } = useAuth();
  return (
    <div>
      <PageHeader icon={Gear} title="Settings"
        subtitle="Account, roles, languages, security and studio preferences." />

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel>
          <div className="flex items-center gap-2 mb-4"><UsersThree size={18} weight="duotone" className="text-[#00E5FF]" /><span className="font-display font-bold text-white">Account & Roles</span></div>
          <div className="flex items-center gap-4 mb-4">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#FF007F] flex items-center justify-center font-bold text-black">{(user?.name || "O")[0]}</div>
            <div><div className="text-white font-medium">{user?.name}</div><div className="text-xs text-zinc-500">{user?.email}</div></div>
            <Tag className="ml-auto">{user?.role}</Tag>
          </div>
          <p className="text-xs text-zinc-500">Role-based access supports Owner, Editor and Reviewer roles. Owner approval is required by default for full episodes, translations and expensive generations.</p>
          <button data-testid="settings-logout" onClick={logout} className="mt-4 rounded-full border border-[#FF3366]/40 text-[#FF3366] px-4 py-2 text-sm hover:bg-[#FF3366]/10 transition-colors">Sign out</button>
        </Panel>

        <Panel>
          <div className="flex items-center gap-2 mb-4"><Globe size={18} weight="duotone" className="text-[#00FF9D]" /><span className="font-display font-bold text-white">Languages</span></div>
          <div className="flex flex-wrap gap-2">{LANGS.map((l) => <Tag key={l}>{l}</Tag>)}</div>
          <p className="text-xs text-zinc-500 mt-3">Architecture supports adding more languages later without a rebuild.</p>
        </Panel>

        <Panel>
          <div className="flex items-center gap-2 mb-4"><ShieldCheck size={18} weight="duotone" className="text-[#00E5FF]" /><span className="font-display font-bold text-white">Security</span></div>
          <div className="space-y-2">
            {SECURITY.map((s) => (
              <div key={s} className="flex items-center gap-2 text-sm text-zinc-300"><Lock size={14} weight="duotone" className="text-[#00FF9D]" /> {s}</div>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-3">Provider API keys are stored server-side only — never exposed in frontend code.</p>
        </Panel>

        <Panel>
          <div className="flex items-center gap-2 mb-4"><ClockCounterClockwise size={18} weight="duotone" className="text-[#FFB800]" /><span className="font-display font-bold text-white">Approval Workflow</span></div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {["GENERATED", "REVIEW", "APPROVED", "SCHEDULED", "PUBLISHED"].map((s, i) => (
              <span key={s} className="flex items-center gap-2">
                <StatusBadge status={s === "GENERATED" ? "GENERATED" : s === "REVIEW" ? "REVIEW" : s} />
                {i < 4 && <span className="text-zinc-600">→</span>}
              </span>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-3">Owner approval required by default. Configurable automation can be enabled later.</p>
        </Panel>

        <Panel>
          <div className="flex items-center gap-2 mb-4"><HardDrives size={18} weight="duotone" className="text-[#8B5CF6]" /><span className="font-display font-bold text-white">AI Mode & Local AI</span></div>
          <p className="text-sm text-zinc-400 mb-3">Choose Free-First to prefer open-source Studio Local and be asked before any paid provider is used. Attach a self-hosted GPU endpoint, check hardware and licences, and run the Test Lab.</p>
          <div className="flex flex-wrap gap-2">
            <Link to="/local" data-testid="settings-local" className="inline-flex items-center gap-2 rounded-full bg-magic px-4 py-2 text-sm font-semibold text-black">Open Local AI</Link>
            <Link to="/local-lab" className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#1C1C1C] px-4 py-2 text-sm text-white hover:border-white/30 transition-colors">Test Lab</Link>
          </div>
        </Panel>
      </div>
    </div>
  );
}
